import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook for audio recording using MediaRecorder API
 * @param {Object} options - Configuration options
 * @returns {Object} Audio recording state and controls
 */
const useAudioRecorder = (options = {}) => {
  const {
    maxDuration = 60, // Maximum recording duration in seconds
    audioFormat = 'audio/webm', // Preferred audio format
    onRecordingComplete = null,
    onError = null,
    onTimeUpdate = null
  } = options;

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBase64, setAudioBase64] = useState(null);
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('prompt'); // 'granted', 'denied', 'prompt'
  const [audioFormat_, setAudioFormat] = useState(audioFormat);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Check for browser support
  useEffect(() => {
    // `Boolean(...)` no es cosmético: sin él, la expresión evalúa al propio
    // constructor `window.MediaRecorder`, y `setState(fn)` lo trata como un
    // updater y lo INVOCA sin `new` → "Failed to construct 'MediaRecorder'".
    // Con eso, toda pantalla que montara este hook explotaba en cualquier
    // navegador real (jsdom no define MediaRecorder, así que ningún test lo veía).
    setIsSupported(Boolean(
      typeof window !== 'undefined' &&
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia &&
      window.MediaRecorder
    ));

    // Check microphone permission status
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: 'microphone' })
        .then((permissionStatus) => {
          setPermissionStatus(permissionStatus.state);
          permissionStatus.onchange = () => {
            setPermissionStatus(permissionStatus.state);
          };
        })
        .catch((err) => {
          console.warn('Could not check microphone permission:', err);
        });
    }
  }, []);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        setPaused(false);
        console.log('Recording stopped');
      } catch (err) {
        console.error('Failed to stop recording:', err);
      }
    }

    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, [isRecording]);

  // Keep latest audioUrl/stopRecording in refs so the unmount-only cleanup
  // effect below never closes over stale values (#433: this used to revoke
  // the audioUrl captured at MOUNT time — i.e. always null — leaking the
  // object URL of any audio actually recorded during the component's life).
  const audioUrlRef = useRef(audioUrl);
  useEffect(() => {
    audioUrlRef.current = audioUrl;
  }, [audioUrl]);

  const stopRecordingRef = useRef(null);
  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  });

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopRecordingRef.current?.();
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
    };
  }, []);

  // Update recording time
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setRecordingTime(elapsed);

        if (onTimeUpdate) {
          onTimeUpdate(elapsed);
        }

        // Auto-stop at max duration
        if (elapsed >= maxDuration) {
          stopRecording();
        }
      }, 100); // Update every 100ms for smooth timer
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording, isPaused, maxDuration, onTimeUpdate, stopRecording]);

  // Request microphone permission
  const requestPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Stop the stream immediately (we just wanted permission)
      stream.getTracks().forEach(track => track.stop());

      setPermissionStatus('granted');
      setError(null);
      return true;
    } catch (err) {
      setPermissionStatus('denied');
      const errorMessage = err.name === 'NotAllowedError'
        ? 'Microphone permission denied. Please allow microphone access.'
        : 'Failed to access microphone. Please check your microphone.';
      setError(errorMessage);
      if (onError) {
        onError(err);
      }
      return false;
    }
  }, [onError]);

  // Determine the best supported format
  const getSupportedMimeType = useCallback(() => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
      'audio/mpeg',
      'audio/wav'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    // Fallback to default
    return '';
  }, []);

  // Convert blob to base64
  const blobToBase64 = useCallback((blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:audio/webm;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError('Audio recording is not supported in this browser');
      return false;
    }

    if (permissionStatus === 'denied') {
      setError('Microphone permission is denied. Please enable it in browser settings.');
      return false;
    }

    try {
      // Reset state
      setError(null);
      setRecordingTime(0);
      setAudioBlob(null);
      setAudioUrl(null);
      setAudioBase64(null);
      chunksRef.current = [];

      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100 // CD quality
        }
      });

      streamRef.current = stream;
      setPermissionStatus('granted');

      // Determine the best format
      const mimeType = getSupportedMimeType();
      setAudioFormat(mimeType || 'audio/webm');

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType || undefined,
        audioBitsPerSecond: 128000 // 128 kbps for good quality
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Create blob from chunks
        const audioBlob = new Blob(chunksRef.current, {
          type: mimeType || 'audio/webm'
        });

        setAudioBlob(audioBlob);

        // Create URL for playback
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        // Convert to base64
        try {
          const base64 = await blobToBase64(audioBlob);
          setAudioBase64(base64);

          if (onRecordingComplete) {
            onRecordingComplete({
              blob: audioBlob,
              url,
              base64,
              duration: recordingTime,
              format: mimeType || 'audio/webm'
            });
          }
        } catch (err) {
          console.error('Failed to convert audio to base64:', err);
          setError('Failed to process audio recording');
        }

        // Clean up
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setError('Recording failed. Please try again.');
        setIsRecording(false);
        if (onError) {
          onError(event.error);
        }
      };

      mediaRecorderRef.current = mediaRecorder;

      // Start recording
      mediaRecorder.start(1000); // Capture data every second
      setIsRecording(true);
      startTimeRef.current = Date.now();

      console.log('Recording started with format:', mimeType || 'default');
      return true;

    } catch (err) {
      console.error('Failed to start recording:', err);
      const errorMessage = err.name === 'NotAllowedError'
        ? 'Microphone permission denied. Please allow microphone access.'
        : err.name === 'NotFoundError'
        ? 'No microphone found. Please connect a microphone.'
        : 'Failed to start recording. Please check your microphone.';

      setError(errorMessage);
      setIsRecording(false);

      if (onError) {
        onError(err);
      }

      return false;
    }
  }, [isSupported, permissionStatus, getSupportedMimeType, blobToBase64, onRecordingComplete, onError, recordingTime]);

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      setPaused(true);
      console.log('Recording paused');
    }
  }, [isRecording, isPaused]);

  // Resume recording
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      setPaused(false);
      startTimeRef.current = Date.now() - (recordingTime * 1000); // Adjust start time
      console.log('Recording resumed');
    }
  }, [isRecording, isPaused, recordingTime]);

  // Cancel recording
  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      try {
        // Stop without saving
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.stop();
      } catch (err) {
        console.error('Failed to cancel recording:', err);
      }
    }

    // Clean up
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Reset state
    setIsRecording(false);
    setPaused(false);
    setRecordingTime(0);
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioBase64(null);
    chunksRef.current = [];

    console.log('Recording cancelled');
  }, []);

  // Reset recording
  const resetRecording = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }

    setRecordingTime(0);
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioBase64(null);
    setError(null);
    chunksRef.current = [];
  }, [audioUrl]);

  // Format time for display
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  return {
    // State
    isRecording,
    isPaused,
    recordingTime,
    formattedTime: formatTime(recordingTime),
    audioBlob,
    audioUrl,
    audioBase64,
    error,
    isSupported,
    permissionStatus,
    audioFormat: audioFormat_,
    maxDuration,

    // Actions
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    resetRecording,
    requestPermission,

    // Utilities
    formatTime
  };
};

export default useAudioRecorder;